import * as React from 'react';
import {
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  ThumbUp as ThumbUpIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
  DoubleArrow as DoubleArrowIcon,
} from '@mui/icons-material';
import { ToolbarIconType } from '../../../types';

export type TableRowKey = {
  id: string;
};

export type TableActionDisplay = {
  tooltip: string;
  aria: string;
  iconType: ToolbarIconType;
};

export type TableAction = {
  action: () => void;
  display: TableActionDisplay;
};

export type TableRowProps = {
  values: { [key: string]: string };
  disabled?: boolean;
  actions?: TableAction[];
};

const renderIcon = (type: ToolbarIconType) => {
  switch (type) {
    case 'DELETE':
      return <DeleteIcon />;
    case 'EDIT':
      return <EditIcon />;
    case 'SEND':
      return <SendIcon />;
    case 'REFRESH':
      return <RefreshIcon />;
    case 'CONFIRM':
      return <ThumbUpIcon />;
    case 'VIEW':
      return <VisibilityIcon />;
    case 'COPY':
      return <FileCopyIcon />;
    case 'CONFIRM_AND_SUBMIT':
      return <DoubleArrowIcon />;
    default:
      return '';
  }
};

const CustomTableRow = ({
  values,
  disabled = false,
  actions = [],
}: TableRowProps) => {
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const key = values['id'];
  const onActionMenuClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };
  const onActionMenuClose = () => {
    setAnchorEl(null);
  };
  return (
    <TableRow hover tabIndex={-1} key={key}>
      {Object.entries(values).map(([key, value], index) => {
        return (
          <TableCell key={key} id={key} scope={index === 0 ? 'row' : 'none'}>
            {value}
          </TableCell>
        );
      })}
      {actions.length > 0 && (
        <TableCell key={actions[0].display.tooltip} padding="none">
          <Tooltip
            title={`${actions[0].display.tooltip}`}
            key={actions[0].display.aria}
          >
            <span>
              <IconButton
                aria-label={actions[0].display.aria}
                onClick={actions[0].action}
                disabled={disabled}
                size="small"
              >
                {renderIcon(actions[0].display.iconType)}
              </IconButton>
            </span>
          </Tooltip>
          {actions.length === 2 && (
            <Tooltip
              title={`${actions[1].display.tooltip}`}
              key={actions[1].display.aria}
            >
              <span>
                <IconButton
                  aria-label={actions[1].display.aria}
                  onClick={actions[1].action}
                  disabled={disabled}
                  size="small"
                >
                  {renderIcon(actions[1].display.iconType)}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {actions.length > 2 && (
            <>
              <Tooltip title="More actions" key="more">
                <span>
                  <IconButton
                    aria-label="more"
                    aria-haspopup="true"
                    aria-controls="action-menu"
                    onClick={onActionMenuClick}
                    disabled={disabled}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={onActionMenuClose}
              >
                {actions.slice(1).map((action) => (
                  <MenuItem
                    key={action.display.tooltip}
                    onClick={() => {
                      action.action();
                      onActionMenuClose();
                    }}
                  >
                    {action.display.tooltip}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default CustomTableRow;
